import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Alert,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Badge
} from '@mui/material';
import { 
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  CallMade as CallMadeIcon,
  ContactSupport as ContactSupportIcon
} from '@mui/icons-material';
import { api } from '../../../../services/api';

// Import styled components
import {
  DetailSection,
  DetailContainer
} from '../../TripDetailPanel.styles';
import {
  StatusChip
} from '../../styles/Common.styles';
import styles from '../callbackRequest/CallbackRequestPanel.module.css';

interface ContactRequest {
  id: number;
  customer: any | null;
  name: string;
  email: string;
  mobileNumber: string;
  subject: string;
  message: string;
  status: string;
  response?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  completedAt?: string | null;
  completedBy?: string | null;
}

interface ContactResponse {
  status: boolean;
  message: string;
  data: ContactRequest[];
}

const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  // 'PENDING': 'warning',
  'IN_PROGRESS': 'info',
  'COMPLETED': 'success',
  'CANCELLED': 'error',
  // 'RESPONDED': 'success'
};

const ContactRequestPanel: React.FC = () => {
  const [contacts, setContacts] = useState<ContactRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedContact, setSelectedContact] = useState<ContactRequest | null>(null);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [updateStatus, setUpdateStatus] = useState('');
  const [updateResponse, setUpdateResponse] = useState('');
  const [updating, setUpdating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const hasFetched = useRef(false);

  const statusOptions = [
    // { value: 'PENDING', label: 'Pending' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    // { value: 'RESPONDED', label: 'Responded' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'CANCELLED', label: 'Cancelled' }
  ];

  // Fetch contact data when component mounts
  const fetchContacts = async () => {
    try {
      setLoading(!refreshing);
      setError(null);
      
      const response = await api.getContactRequests() as ContactResponse | ContactRequest[];
      
      // Handle the actual API response structure
      let contactData: ContactRequest[] = [];
      if (response && typeof response === 'object' && 'status' in response && Array.isArray(response.data)) {
        contactData = response.data;
      } else if (Array.isArray(response)) {
        contactData = response;
      }
      
      setContacts(contactData);
    } catch (err) {
      console.error('Error fetching contact requests:', err);
      setError('Failed to load contact requests');
      setContacts([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchContacts();
    }
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchContacts();
  };

  const handleUpdateContact = (contact: ContactRequest) => {
    setSelectedContact(contact);
    setUpdateStatus(contact.status);
    setUpdateResponse(contact.response || '');
    setUpdateDialogOpen(true);
  };

  const handleStatusUpdate = async () => {
    if (!selectedContact) return;

    try {
      setUpdating(true);
      setError(null);
      
      // Send simplified payload structure
      const response = await api.updateContactRequestStatus(
        selectedContact.id, 
        updateStatus, 
        updateResponse,
        {
          id: selectedContact.id,
          name: selectedContact.name,
          status: updateStatus,
          response: updateResponse
        }
      );      
      // Update local state with the new values
      setContacts(prev => prev.map(contact => 
        contact.id === selectedContact.id 
          ? { 
              ...contact, 
              status: updateStatus, 
              response: updateResponse,
              updatedAt: new Date().toISOString()
            }
          : contact
      ));
      
      setUpdateDialogOpen(false);
      setSelectedContact(null);
      setSuccessMessage('Contact request status updated successfully');
      
      // Auto-refresh data from server to ensure UI is in sync
      setTimeout(() => {
        fetchContacts();
        setSuccessMessage(null);
      }, 1000);
      
    } catch (err: any) {
      console.error('Error updating contact status:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update contact status';
      setError(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
      // case 'RESPONDED':
      //   return <CheckIcon fontSize="small" />;
      case 'CANCELLED':
        return <CancelIcon fontSize="small" />;
      case 'IN_PROGRESS':
        return <CallMadeIcon fontSize="small" />;
      default:
        return <TimeIcon fontSize="small" />;
    }
  };

  if (loading && !refreshing) {
    return (
      <DetailContainer>
        <DetailSection>
          <div className={styles.loadingContainer}>
            <CircularProgress size={40} />
            <Typography variant="body1" className={styles.loadingText}>
              Loading contact requests...
            </Typography>
          </div>
        </DetailSection>
      </DetailContainer>
    );
  }

  return (
    <DetailContainer>
      <DetailSection className={styles.detailSection}>
        {/* Header */}
        <div className={styles.headerContainer}>
          <div className={styles.titleContainer}>
            <Typography variant="h4" gutterBottom className={styles.title}>
              Contact Requests
            </Typography>
            <Typography variant="subtitle1" className={styles.subtitle}>
              Manage customer contact requests efficiently
            </Typography>
          </div>
          <div className={styles.actionsContainer}>
            <Tooltip title="Refresh Data">
              <IconButton 
                onClick={handleRefresh} 
                disabled={refreshing}
                className={styles.refreshButton}
              >
                {refreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
            <div className={styles.badgeContainer}>
              <Badge 
                badgeContent={contacts.filter(c => c.status === 'PENDING').length} 
                color="error"
                overlap="rectangular"
                className={styles.badge}
              >
                <Chip 
                  label={`${contacts.length} Total`} 
                  color="primary" 
                  variant="outlined"
                  size="medium"
                  className={styles.totalChip}
                />
              </Badge>
            </div>
          </div>
        </div>

        {error && (
          <Alert severity="error" className={styles.alertContainer} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" className={styles.alertContainer} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {contacts.length === 0 ? (
          <Paper className={styles.emptyStateContainer}>
            <ContactSupportIcon className={styles.emptyStateIcon} />
            <Typography variant="h6" className={styles.emptyStateTitle} sx={{ color: 'white' }}>
              No contact requests found
            </Typography>
            <Typography variant="body2" className={styles.emptyStateSubtitle} sx={{ color: 'white' }}>
              Customer contact requests will appear here when submitted
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} className={styles.tableContainer}>
            <Table stickyHeader>
              <TableHead>
                <TableRow className={styles.tableHeader}>
                  <TableCell className={styles.tableHeaderCell}>Name</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Email</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Mobile</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Status</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Customer Message</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Operator Notes</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Submitted At</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow 
                    key={contact.id} 
                    hover
                    className={`${styles.tableRow} ${
                      ['COMPLETED', 'CANCELLED', 'RESPONDED'].includes(contact.status) 
                        ? styles.tableRowCompleted 
                        : ''
                    }`}
                  >
                    <TableCell>
                      <Typography variant="body1" className={styles.customerName}>
                        {contact.name || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <div className={styles.iconContainer}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {contact.email || 'N/A'}
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={styles.iconContainer}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {contact.mobileNumber || 'N/A'}
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusChip 
                        icon={getStatusIcon(contact.status)}
                        label={contact.status || 'PENDING'} 
                        color={statusColors[contact.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {contact.message || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {contact.response || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <div className={styles.dateContainer}>
                        <TimeIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {formatDate(contact.createdAt)}
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={styles.actionsStack}>
                        <Tooltip title="Update Status">
                          <IconButton 
                            size="small" 
                            onClick={() => handleUpdateContact(contact)}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {contact.email && (
                          <Tooltip title="Send Email">
                            <IconButton 
                              size="small" 
                              href={`mailto:${contact.email}?subject=Re: ${contact.subject}&body=Hi ${contact.name},`}
                              color="success"
                            >
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {contact.mobileNumber && (
                          <Tooltip title="Call Customer">
                            <IconButton 
                              size="small" 
                              href={`tel:${contact.mobileNumber}`}
                              color="info"
                            >
                              <PhoneIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Update Status Dialog */}
        <div className={styles.dialogContainer}>
          <Dialog 
            open={updateDialogOpen} 
            onClose={() => setUpdateDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                backgroundColor: '#2a2a2a',
                color: 'white'
              }
            }}
          >
            <DialogTitle sx={{ color: 'white' }}>
              Update Contact Request Status
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: '#2a2a2a', color: 'white' }}>
              <div className={styles.dialogContent}>
                <Typography variant="subtitle1" className={styles.customerInfo}>
                  Name: {selectedContact?.name || 'Unknown'}
                </Typography>
                <Typography variant="body2" className={styles.mobileInfo}>
                  Email: {selectedContact?.email}
                </Typography>
                {/* <Typography variant="body2" className={styles.mobileInfo}>
                  Subject: {selectedContact?.subject}
                </Typography> */}
                {selectedContact?.message && (
                  <Typography variant="body2" className={styles.mobileInfo} sx={{ mb: 2 }}>
                    Message: {selectedContact.message}
                  </Typography>
                )}
                
                <FormControl fullWidth className={styles.statusFormControl}>
                  <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Status</InputLabel>
                  <Select
                    value={updateStatus}
                    onChange={(e) => setUpdateStatus(e.target.value)}
                    label="Status"
                    sx={{
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.5)'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3b82f6'
                      }
                    }}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  className={styles.notesField}
                  multiline
                  rows={3}
                  label="Response (Optional)"
                  value={updateResponse}
                  onChange={(e) => setUpdateResponse(e.target.value)}
                  placeholder="Add your response to this contact request..."
                  sx={{
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                    }
                  }}
                />
              </div>
            </DialogContent>
            <DialogActions sx={{ backgroundColor: '#2a2a2a' }}>
              <Button onClick={() => setUpdateDialogOpen(false)} sx={{ color: 'white' }}>
                Cancel
              </Button>
              <Button 
                onClick={handleStatusUpdate} 
                variant="contained"
                disabled={updating || !updateStatus}
                sx={{ backgroundColor: '#3b82f6' }}
              >
                {updating ? <CircularProgress size={20} /> : 'Update'}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </DetailSection>
    </DetailContainer>
  );
};

export default ContactRequestPanel;
